import React, { useEffect, useState } from 'react'
import { Box, Button, IconButton, Menu, MenuItem, Paper, Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { router, usePage } from '@inertiajs/react'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

function LineItemRow({ li }) {
  const [open, setOpen] = useState(false)
  const hasDistributions = li.unit_distributions && li.unit_distributions.length > 0
  const hasBudget = li.budgeted_amount != null && li.budgeted_amount !== ''
  const variance = hasBudget
    ? (parseFloat(li.amount) - parseFloat(li.budgeted_amount))
    : null

  return (
    <>
      <TableRow data-testid={`line-item-row-${li.id}`}>
        <TableCell sx={{ width: 48, p: 0.5 }}>
          {hasDistributions && (
            <IconButton size="small" onClick={() => setOpen(!open)} data-testid={`expand-btn-${li.id}`}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
          )}
        </TableCell>
        <TableCell>{li.cost_category_name}</TableCell>
        <TableCell align="right" data-testid={`li-dk-${li.id}`}>{li.distribution_key_name}</TableCell>
        <TableCell align="right" data-testid={`li-budgeted-${li.id}`}>
          {hasBudget ? <FormattedCurrency value={li.budgeted_amount} /> : <Typography component="span" variant="body2" color="text.disabled">—</Typography>}
        </TableCell>
        <TableCell align="right"><FormattedCurrency value={li.amount} /></TableCell>
        <TableCell align="right" data-testid={`li-variance-${li.id}`} sx={{
          fontWeight: 600,
          color: variance == null ? 'text.disabled' : variance > 0 ? 'error.main' : variance < 0 ? 'success.main' : 'inherit'
        }}>
          {variance == null ? '—' : <FormattedCurrency value={variance.toFixed(2)} />}
        </TableCell>
      </TableRow>
      {hasDistributions && open && li.unit_distributions.map((ud) => {
        const udHasBudget = ud.budgeted_amount != null && ud.budgeted_amount !== ''
        const udVariance = udHasBudget
          ? (parseFloat(ud.amount) - parseFloat(ud.budgeted_amount))
          : null
        return (
          <TableRow key={ud.unit_number} data-testid={`distribution-${li.id}-${ud.unit_number}`}
            sx={{ backgroundColor: 'background.subtle' }}>
            <TableCell sx={{ width: 48 }} />
            <TableCell sx={{ fontSize: '0.75rem', pl: 4 }}>
              {ud.unit_number}
              {ud.unit_location && (
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {ud.unit_location}
                </Typography>
              )}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>{ud.attribute_value || ''}</TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}
              data-testid={`ud-budgeted-${li.id}-${ud.unit_number}`}>
              {udHasBudget ? <FormattedCurrency value={ud.budgeted_amount} /> : ''}
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem' }}>
              <FormattedCurrency value={ud.amount} />
            </TableCell>
            <TableCell align="right" sx={{ fontSize: '0.75rem',
              color: udVariance == null ? 'text.disabled' : udVariance > 0 ? 'error.main' : udVariance < 0 ? 'success.main' : 'inherit'
            }} data-testid={`ud-variance-${li.id}-${ud.unit_number}`}>
              {udVariance == null ? '' : <FormattedCurrency value={udVariance.toFixed(2)} />}
            </TableCell>
          </TableRow>
        )
      })}
    </>
  )
}

export default function Show({ property, settlement, budget_plan = null, line_items = [], unit_results = [], generated_documents = [] }) {
  const { props } = usePage()
  const tenant = props.tenant || {}
  const basePath = `/m/${tenant.slug}/properties/${property.id}`
  const [pdfMenuAnchor, setPdfMenuAnchor] = useState(null)

  const hasPendingDocuments = generated_documents.some(
    (doc) => doc.status === 'pending' || doc.status === 'generating'
  )

  useEffect(() => {
    if (!hasPendingDocuments) return

    const interval = setInterval(() => {
      router.reload({ only: ['generated_documents'] })
    }, 3000)

    return () => clearInterval(interval)
  }, [hasPendingDocuments])

  const handleCalculate = () => {
    router.patch(`${basePath}/settlements/${settlement.id}/calculate`)
  }

  const handleApprove = () => {
    router.patch(`${basePath}/settlements/${settlement.id}/approve_settlement`)
  }

  const handleFinalize = () => {
    router.patch(`${basePath}/settlements/${settlement.id}/finalize`)
  }

  const handleGeneratePdf = (documentType) => {
    setPdfMenuAnchor(null)
    router.post(`${basePath}/settlements/${settlement.id}/generate_abrechnung`, {
      document_type: documentType,
    })
  }

  const showPdfMenu = ['approved', 'sent', 'closed'].includes(settlement.status)

  return (
    <Box>
      <PageHeader
        title={settlement.name}
        breadcrumbs={[
          { label: property.name, href: basePath },
          { label: 'Hausgeldabrechnungen', href: `${basePath}/settlements` },
          { label: settlement.name },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {settlement.status === 'draft' && (
              <>
                <Button variant="outlined" size="small" onClick={handleCalculate} data-testid="calculate-btn">
                  Berechnen
                </Button>
                <Button variant="outlined" size="small" startIcon={<EditIcon />}
                  onClick={() => router.visit(`${basePath}/settlements/${settlement.id}/edit`)}
                  data-testid="edit-btn"
                >
                  Bearbeiten
                </Button>
              </>
            )}
            {settlement.status === 'calculated' && (
              <Button variant="contained" size="small" onClick={handleApprove} data-testid="approve-btn">
                Genehmigen
              </Button>
            )}
            {settlement.status === 'approved' && (
              <Button variant="contained" size="small" onClick={handleFinalize} data-testid="finalize-btn">
                Finalisieren
              </Button>
            )}
            {showPdfMenu && (
              <>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<PictureAsPdfIcon />}
                  onClick={(e) => setPdfMenuAnchor(e.currentTarget)}
                  data-testid="pdf-menu-btn"
                >
                  PDF generieren
                </Button>
                <Menu
                  anchorEl={pdfMenuAnchor}
                  open={Boolean(pdfMenuAnchor)}
                  onClose={() => setPdfMenuAnchor(null)}
                >
                  <MenuItem onClick={() => handleGeneratePdf('gesamtabrechnung')} data-testid="pdf-gesamt">
                    Gesamtabrechnung
                  </MenuItem>
                  <MenuItem onClick={() => handleGeneratePdf('einzelabrechnung')} data-testid="pdf-einzel">
                    Einzelabrechnungen (alle Einheiten)
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        }
      />

      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge type="settlement_status" value={settlement.status} />
          <Typography variant="body2" color="text.secondary">
            Geschäftsjahr: {settlement.fiscal_year_label}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Abrechnungsart: {settlement.settlement_type}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 3, mt: 1, flexWrap: 'wrap' }}>
          <Typography variant="body2" fontWeight={600}>Gesamtkosten: <FormattedCurrency value={settlement.total_costs} align="left" /></Typography>
          {budget_plan && (
            <Typography variant="body2" fontWeight={600} data-testid="bp-total">
              Wirtschaftsplan ({budget_plan.name}): <FormattedCurrency value={budget_plan.total_amount} align="left" />
            </Typography>
          )}
        </Box>
      </Paper>

      {line_items.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Kostenpositionen</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ width: 48 }} />
                  <TableCell sx={{ fontWeight: 600 }}>Kostenkategorie</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Verteilerschlüssel</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Wirtschaftsplan</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Abrechnung</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Differenz</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {line_items.map((li) => (
                  <LineItemRow key={li.id} li={li} />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {unit_results.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Ergebnisse je Einheit</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 3 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Einheit</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Wirtschaftsplan</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Kostenanteil</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Differenz</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Vorauszahlungen</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {unit_results.map((ur) => {
                  const urHasBudget = ur.budgeted_cost_share != null && ur.budgeted_cost_share !== ''
                  const urVariance = urHasBudget
                    ? (parseFloat(ur.cost_share) - parseFloat(ur.budgeted_cost_share))
                    : null
                  return (
                    <TableRow key={ur.id} data-testid={`unit-result-${ur.id}`}>
                      <TableCell>{ur.unit_number}</TableCell>
                      <TableCell align="right" data-testid={`ur-budgeted-${ur.id}`}>
                        {urHasBudget ? <FormattedCurrency value={ur.budgeted_cost_share} /> : <Typography component="span" variant="body2" color="text.disabled">—</Typography>}
                      </TableCell>
                      <TableCell align="right"><FormattedCurrency value={ur.cost_share} /></TableCell>
                      <TableCell align="right" data-testid={`ur-variance-${ur.id}`} sx={{
                        fontWeight: 600,
                        color: urVariance == null ? 'text.disabled' : urVariance > 0 ? 'error.main' : urVariance < 0 ? 'success.main' : 'inherit'
                      }}>
                        {urVariance == null ? '—' : <FormattedCurrency value={urVariance.toFixed(2)} />}
                      </TableCell>
                      <TableCell align="right"><FormattedCurrency value={ur.advance_payments} /></TableCell>
                      <TableCell align="right" sx={{
                        fontWeight: 600,
                        color: parseFloat(ur.balance) > 0 ? 'error.main' : parseFloat(ur.balance) < 0 ? 'success.main' : 'inherit'
                      }}>
                        <FormattedCurrency value={ur.balance} />
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}

      {generated_documents.length > 0 && (
        <>
          <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>Generierte Dokumente</Typography>
          <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: 'background.subtle' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Titel</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Erstellt</TableCell>
                  <TableCell sx={{ fontWeight: 600 }} align="right">Aktion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {generated_documents.map((doc) => (
                  <TableRow key={doc.id} data-testid={`generated-doc-${doc.id}`}>
                    <TableCell>{doc.title}</TableCell>
                    <TableCell>
                      <StatusBadge type="generated_document_status" value={doc.status} />
                    </TableCell>
                    <TableCell>{doc.generated_at ? new Date(doc.generated_at).toLocaleString('de-DE') : '—'}</TableCell>
                    <TableCell align="right">
                      {doc.download_url && (
                        <Button
                          variant="text"
                          size="small"
                          href={doc.download_url}
                          data-testid={`download-btn-${doc.id}`}
                        >
                          Download
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  )
}
